import { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { useLocation } from 'react-router-dom';
import { ReactComponent as GenerateIconColor } from '../../assets/24x24/ai-generate-color.svg';
import { ReactComponent as GenerateIconNoColor } from '../../assets/24x24/ai-generate-no-color.svg';
import { ReactComponent as AiIconMagenta } from '../../assets/ai-generate-magenta.svg';
import SvgIcon from '../SvgIcon';
import { LoadingButton } from '../Button';
import { Props as LoadingButtonProps } from '../Button/LoadingButton';
import Menu from '../Menu/Menu';
import { ROUTE_CHAT_CONFIGS } from '../AiChat/constants';
import { Suggestion } from '../AiChat/types';
import useAiChatPanel from '../../hooks/useAiChatPanel';
import { AgentContext } from '../../types';
import { fontSize, fontWeight, radius, shadow, space } from '../../style/utils';

interface Props extends Omit<LoadingButtonProps, 'variant'> {
  coloredIcon?: boolean;
  variant?: 'default' | 'widget';
  suggestions?: Suggestion[];
  startMessage?: string;
  context?: AgentContext;
}

const suggestionLabel = (s: Suggestion): string =>
  typeof s === 'string' ? s : s.label;

const gradientBorderStyle = css`
  border: 1px solid transparent;
  background-origin: border-box;
  background-clip: padding-box, border-box;
  box-shadow: ${shadow('sm')};
`;

const gradientStyle = css`
  ${gradientBorderStyle}

  background-image: linear-gradient(
      ${({ theme }) => theme.palette.surface.base},
      ${({ theme }) => theme.palette.surface.base}
    ),
    linear-gradient(
      to right,
      ${({ theme }) => theme.palette.accent.aiGradient.start},
      ${({ theme }) => theme.palette.accent.aiGradient.middle},
      ${({ theme }) => theme.palette.accent.aiGradient.end}
    );

  :hover {
    ${gradientBorderStyle}
    background-image: linear-gradient(
        ${({ theme }) => theme.palette.surface.shadeLight},
        ${({ theme }) => theme.palette.surface.shadeLight}
      ),
      linear-gradient(to right, ${({ theme }) =>
      theme.palette.accent.aiGradient.start}, ${({ theme }) =>
      theme.palette.accent.aiGradient.middle}, ${({ theme }) =>
      theme.palette.accent.aiGradient.end});
  }

  :disabled {
    ${gradientBorderStyle}
    background-image: linear-gradient(
      ${({ theme }) => theme.palette.surface.base},
      ${({ theme }) => theme.palette.surface.base}
    ),
      linear-gradient(to right, ${({ theme }) =>
      theme.palette.accent.aiGradient.start}, ${({ theme }) =>
      theme.palette.accent.aiGradient.middle}, ${({ theme }) =>
      theme.palette.accent.aiGradient.end});
  }
`;

const borderlessStyle = css`
  border: 1px solid transparent;
  box-shadow: none;
  background: ${({ theme }) => theme.tokens.components.aiButton.bg};

  :hover {
    border: 1px solid transparent;
    background: ${({ theme }) => theme.tokens.components.aiButton.hoverBg};
  }

  :active {
    background: ${({ theme }) => theme.tokens.components.aiButton.pressedBg};
  }

  :disabled {
    border: 1px solid transparent;
    background: ${({ theme }) => theme.tokens.components.aiButton.bg};
  }
`;

const defaultStyle = css`
  ${({ theme }) =>
    theme.tokens.components.aiButton.gradientBorder
      ? gradientStyle
      : borderlessStyle}
  flex-shrink: 0;

  .MuiButton-startIcon {
    overflow: visible;
    margin-right: ${space('smMinus')};

    > svg {
      overflow: visible;
    }
  }
`;

const widgetStyle = css`
  font-size: ${fontSize('sm')};
  font-weight: ${fontWeight('regular')};
  background: ${({ theme }) => theme.palette.surface.subtle};
  color: ${({ theme }) => theme.palette.content.primary};
  padding: ${space('xs', 'sm')};
  border-radius: ${radius('lg')};
  height: fit-content;
  line-height: normal;
  gap: ${space('xs')};
  min-width: auto;

  .MuiButton-startIcon,
  .MuiButton-endIcon {
    margin: unset;
  }

  :hover {
    background: ${({ theme }) => theme.palette.surface.shade};
    color: ${({ theme }) => theme.palette.content.primary};
  }

  :disabled {
    cursor: not-allowed;
    background: ${({ theme }) => theme.palette.surface.subtle};

    svg {
      opacity: 0.3;
    }
  }
`;

const StyledLoadingButton = styled(LoadingButton)<{
  $buttonVariant: 'default' | 'widget';
}>`
  ${({ $buttonVariant }) =>
    $buttonVariant === 'widget' ? widgetStyle : defaultStyle}
`;

const GenerateAIButton = ({
  coloredIcon = false,
  variant = 'default',
  suggestions,
  startMessage,
  context,
  children = 'Ask AI',
  onClick,
  ...props
}: Props) => {
  const isWidget = variant === 'widget';
  const { openPanel } = useAiChatPanel();
  const { pathname } = useLocation();

  const routeChatConfig = useMemo(() => {
    const match = ROUTE_CHAT_CONFIGS.find((entry) =>
      pathname.includes(entry.pattern),
    );
    return match?.config;
  }, [pathname]);

  const resolvedSuggestions = useMemo(
    () => suggestions ?? routeChatConfig?.suggestions,
    [suggestions, routeChatConfig?.suggestions],
  );

  const OTHER_OPTION = 'Other';

  const menuOptions = useMemo(
    () => [
      ...(resolvedSuggestions?.map((suggestion) => {
        const label = suggestionLabel(suggestion);
        return { value: label, label };
      }) ?? []),
      { value: OTHER_OPTION, label: OTHER_OPTION },
    ],
    [resolvedSuggestions],
  );

  const startIcon = isWidget ? (
    <SvgIcon width={16}>
      <AiIconMagenta />
    </SvgIcon>
  ) : (
    (() => {
      const iconSize = props.size === 'extraSmall' ? 20 : 24;
      const IconComponent = coloredIcon
        ? GenerateIconColor
        : GenerateIconNoColor;
      return <IconComponent width={iconSize} />;
    })()
  );

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick(e);
    } else if (startMessage) {
      openPanel({
        startMessage,
        origin: 'ai_cta',
        ...(context ? { context } : {}),
      });
    }
  };

  const showMenu = resolvedSuggestions && !onClick;

  const button = (
    <StyledLoadingButton
      $buttonVariant={variant}
      variant={isWidget ? undefined : 'outlined'}
      startIcon={startIcon}
      onClick={showMenu ? undefined : handleClick}
      {...props}
    >
      {children}
    </StyledLoadingButton>
  );

  if (showMenu) {
    return (
      <Menu
        menuButton={button}
        options={menuOptions}
        closeOnChange
        sx={{ mt: 1 }}
        onChange={(suggestion) => {
          openPanel({
            startMessage: suggestion === OTHER_OPTION ? undefined : suggestion,
            origin: 'ai_cta',
            ...(context ? { context } : {}),
          });
        }}
      />
    );
  }

  return button;
};

export default GenerateAIButton;
